import { useQuery, useMutation } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { isEmpty, isNil } from 'ramda';
import { useEffect } from 'react';
import { Union } from 'ts-toolbelt';

import type { SvbrgExtendedLineItemsCheck } from '@/generated/gql/graphql';
import { useChatSession } from '~/modules';

import { INTAKE_EXTENDED_LINE_ITEMS_MUTATION } from './IntakeExtendedLineItemsCheckStep.mutation.gql';
import { INTAKE_EXTENDED_LINE_ITEMS_CHECK_QUERY } from './IntakeExtendedLineItemsCheckStep.query.gql';

interface UseExtendedLineItemsCheckStepReturn {
  extendedChecks: Union.Nullable<SvbrgExtendedLineItemsCheck[]> | null;
  loading: boolean;
}

export const useExtendedLineItemsCheckStep = (): UseExtendedLineItemsCheckStepReturn => {
  const session = useChatSession();
  const { onError } = useCustomSnackbar();

  const { data: queryData, loading: queryLoading } = useQuery(INTAKE_EXTENDED_LINE_ITEMS_CHECK_QUERY, {
    variables: { id: session.id },
    // To prevent caching issues that could trigger unnecessary mutations
    fetchPolicy: 'network-only',
  });

  const [runExtendedMutation, { loading: extendedLoading, called: extendedCalled }] = useMutation(
    INTAKE_EXTENDED_LINE_ITEMS_MUTATION,
    {
      refetchQueries: [
        {
          query: INTAKE_EXTENDED_LINE_ITEMS_CHECK_QUERY,
          variables: { id: session.id },
        },
      ],
    },
  );

  // Auto-trigger extended mutation if no data
  useEffect(() => {
    const triggerExtendedCheck = async () => {
      try {
        await runExtendedMutation({
          variables: {
            data: { chatSessionId: session.id },
          },
        });
      } catch (error) {
        onError(error);
      }
    };

    const hasExtendedData = queryData?.sevenbridges?.chatSession?.extendedLineItemChecks;

    if (!queryLoading && (isNil(hasExtendedData) || isEmpty(hasExtendedData)) && !extendedCalled) {
      triggerExtendedCheck();
    }
  }, [queryData, queryLoading, extendedCalled, runExtendedMutation, session.id, onError]);

  const loading = queryLoading || extendedLoading;

  return {
    extendedChecks: queryData?.sevenbridges?.chatSession?.extendedLineItemChecks ?? null,
    loading,
  };
};
